import { useId, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { BackupBar } from "@/components/BackupBar";
import { ToolContainer } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { CurrencySelect, Money, currencySymbol } from "../ecommerce/shared";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "../household/ConfirmDialog";
import { DEFAULT_TRIP, useStored, useTrip } from "./tripStore";
import { DEFAULT_CATEGORIES, checkDays, checkTravelers, computeTravel, type Category } from "./travelBudgetMath";

const TOOL_INFO_CONTENT = {
  what: `Plans and totals trip costs across categories — lodging, food, transport, activities, flights and more, plus any categories of your own. Each line can be per day or one-time, and per person or for the whole group, so a shared hotel room isn't multiplied by the number of travellers. A budget vs actual column shows where you are over or under.`,
  how: `Enter the trip length and number of travellers (whole numbers, at least 1), then set each line's amount and choose "per day / total" and "per person / group". Add custom category rows if you need them. During or after the trip, type what you actually spent in the Actual column. The total, the cost per person and the average per day update as you type. Your numbers are kept for this visit only.`,
  tip: `Add a miscellaneous buffer line for unplanned spending — even careful trip budgets run into a few unexpected costs. Use a hotel room price for Lodging (group), not a per-person share.`,
};

function IntField({ label, value, onChange, error }: { label: string; value: string; onChange: (v: string) => void; error?: string }) {
  const id = useId();
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block">{label}</Label>
      <Input id={id} value={value} inputMode="numeric" onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} aria-describedby={error ? `${id}-err` : undefined} className={error ? "border-destructive" : ""} />
      {error && <p id={`${id}-err`} role="alert" className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}

export default function TravelBudgetPlanner() {
  const [trip, setTrip] = useTrip();
  const [currency, setCurrency] = useStored("fdp-budget-currency", "USD");
  const [categories, setCategories] = useStored<Category[]>("fdp-budget-cats-v2", DEFAULT_CATEGORIES);
  const [nextId, setNextId] = useStored("fdp-budget-nextid-v2", 8);
  const [pending, setPending] = useState<null | "reset" | "clear">(null);
  const sym = currencySymbol(currency);

  const patch = (id: number, p: Partial<Category>) => setCategories((prev) => prev.map((c) => (c.id === id ? { ...c, ...p } : c)));
  const daysCheck = checkDays(trip.days);
  const peopleCheck = checkTravelers(trip.travelers);
  const result = useMemo(() => computeTravel(categories, trip.days, trip.travelers), [categories, trip.days, trip.travelers]);
  const t = result.totals;
  const people = peopleCheck.value ?? 1;
  const sel = "h-9 rounded-md border bg-background px-2 text-xs";

  // Reading is free; copying, printing or saving the plan uses one trial use per visit.
  useLogToolUsage(!!t);

  const addCategory = () => {
    setCategories((prev) => [...prev, { id: nextId, name: "Custom category", perDay: false, perPerson: false, amount: "0", actual: "" }]);
    setNextId(nextId + 1);
  };

  const applyWithUndo = (kind: "reset" | "clear") => {
    const prev = { categories, trip, nextId };
    if (kind === "reset") { setCategories(DEFAULT_CATEGORIES); setNextId(8); setTrip(DEFAULT_TRIP); }
    else setCategories([]);
    setPending(null);
    toast(kind === "reset" ? "Reset to the default trip and categories" : "All categories cleared", {
      duration: 10000,
      action: { label: "Undo", onClick: () => { setCategories(prev.categories); setNextId(prev.nextId); setTrip(prev.trip); } },
    });
  };

  return (
    <ToolContainer>
      <div className="flex justify-end mb-3"><CurrencySelect value={currency} onChange={setCurrency} /></div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <IntField label="Trip Length (days)" value={trip.days} onChange={(v) => setTrip({ ...trip, days: v })} error={daysCheck.error} />
        <IntField label="Travelers" value={trip.travelers} onChange={(v) => setTrip({ ...trip, travelers: v })} error={peopleCheck.error} />
      </div>

      <div data-result>
        <div className="hidden sm:flex items-center gap-2 text-xs text-muted-foreground mb-1 pr-10" aria-hidden="true">
          <span className="w-40">Category</span><span className="w-28">Planned ({sym})</span><span className="w-[94px]" /><span className="w-[110px]" />
          <span className="ml-auto flex gap-4"><span className="w-20 text-right">Plan total</span><span className="w-24">Actual spent</span><span className="w-24 text-right">vs actual</span></span>
        </div>
        <div className="space-y-3">
          {categories.map((c) => {
            const r = result.rows.find((x) => x.id === c.id);
            const label = c.name.trim() || "category";
            return (
              <div key={c.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <Input aria-label={`Category name: ${label}`} className="w-40" value={c.name} onChange={(e) => patch(c.id, { name: e.target.value })} />
                  <Input aria-label={`${label} amount (${sym})`} aria-invalid={!!r?.amountError} className={`w-28 ${r?.amountError ? "border-destructive" : ""}`} inputMode="decimal" value={c.amount} onChange={(e) => patch(c.id, { amount: e.target.value })} />
                  <select aria-label={`${label}: per day or total`} className={sel} value={c.perDay ? "day" : "total"} onChange={(e) => patch(c.id, { perDay: e.target.value === "day" })}><option value="day">per day</option><option value="total">one-time</option></select>
                  <select aria-label={`${label}: per person or group`} className={sel} value={c.perPerson ? "person" : "group"} onChange={(e) => patch(c.id, { perPerson: e.target.value === "person" })}><option value="person">per person</option><option value="group">whole group</option></select>
                  <span className="text-sm text-muted-foreground ml-auto flex items-center gap-4">
                    <span className="w-20 text-right">{r?.planned != null ? <Money n={r.planned} currency={currency} decimals={0} /> : "—"}</span>
                    <Input aria-label={`${label} actual spent (${sym})`} aria-invalid={!!r?.actualError} placeholder="Actual" className={`w-24 h-9 ${r?.actualError ? "border-destructive" : ""}`} inputMode="decimal" value={c.actual} onChange={(e) => patch(c.id, { actual: e.target.value })} />
                    <span className="w-24 text-right text-xs">
                      {r?.variance != null ? (r.variance >= 0
                        ? <span className="text-green-600"><Money n={r.variance} currency={currency} decimals={0} /> under</span>
                        : <span className="text-red-600"><Money n={Math.abs(r.variance)} currency={currency} decimals={0} /> over</span>) : ""}
                    </span>
                  </span>
                  <Button type="button" size="icon" variant="ghost" aria-label={`Remove ${label}`} onClick={() => setCategories((prev) => prev.filter((x) => x.id !== c.id))}><Trash2 className="h-4 w-4" /></Button>
                </div>
                {r?.amountError && <p role="alert" className="text-xs text-destructive mt-1">{r.amountError}</p>}
                {r?.actualError && <p role="alert" className="text-xs text-destructive mt-1">{r.actualError}</p>}
              </div>
            );
          })}
          {!categories.length && <p className="text-sm text-muted-foreground">No categories yet. Add one below.</p>}
        </div>
        <Button type="button" size="sm" variant="outline" className="mt-3" onClick={addCategory}><Plus className="h-4 w-4 mr-1" />Add category</Button>

        {!t && <p role="alert" className="text-sm text-destructive mt-4">Fix the red boxes to see your totals. Trip length and travelers must be whole numbers of at least 1, and amounts can't be negative.</p>}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
          <Card className="p-6 text-center"><p className="text-3xl font-bold text-primary" data-testid="tb-total">{t ? <Money n={t.planned} currency={currency} decimals={0} /> : "—"}</p><p className="text-sm text-muted-foreground mt-1">Total trip cost ({people} traveler{people > 1 ? "s" : ""})</p></Card>
          <Card className="p-6 text-center"><p className="text-3xl font-bold text-primary" data-testid="tb-pp">{t ? <Money n={t.perPerson} currency={currency} decimals={0} /> : "—"}</p><p className="text-sm text-muted-foreground mt-1">Per person</p></Card>
          <Card className="p-6 text-center"><p className="text-3xl font-bold text-primary">{t ? <Money n={t.perDay} currency={currency} decimals={0} /> : "—"}</p><p className="text-sm text-muted-foreground mt-1">Average per day</p></Card>
        </div>
        {t && t.actualCount > 0 && (
          <p className="text-sm text-center mt-3" data-testid="tb-actual">
            Actual spent so far: <Money n={t.actual} currency={currency} decimals={0} /> across {t.actualCount} categor{t.actualCount === 1 ? "y" : "ies"} ·{" "}
            {t.variance >= 0 ? <span className="text-green-600"><Money n={t.variance} currency={currency} decimals={0} /> under plan</span> : <span className="text-red-600"><Money n={Math.abs(t.variance)} currency={currency} decimals={0} /> over plan</span>}
          </p>
        )}
      </div>

      <div className="flex flex-wrap justify-between items-center gap-2 mt-3">
        <p className="text-xs text-muted-foreground">Kept for this visit only (erased when you close the tab). Nothing is sent to a server.</p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setPending("reset")}><RotateCcw className="h-3.5 w-3.5 mr-1" />Reset to defaults</Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setPending("clear")}><Trash2 className="h-3.5 w-3.5 mr-1" />Clear all categories</Button>
        </div>
      </div>
      <ConfirmDialog
        open={pending !== null}
        title={pending === "clear" ? "Clear all categories?" : "Reset to defaults?"}
        body={pending === "clear" ? "This removes every category row. You can undo it right after." : "This restores the default trip length, travelers and categories, and drops your changes. You can undo it right after."}
        confirmLabel={pending === "clear" ? "Clear all" : "Reset"}
        onConfirm={() => pending && applyWithUndo(pending)}
        onCancel={() => setPending(null)}
      />
      <BackupBar tool="travel-budget-planner" prefixes={["fdp-trip-", "fdp-budget-"]} session hideClear />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
